import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Clock, FileText, Inbox, Network, Send } from 'lucide-react';
import InboxPanel from './InboxPanel';
import SendPanel, { SEND_ENTRY_CONTEXT_TYPES } from './SendPanel';
import JourneyPanel from './JourneyPanel';
import TemplatesPanel from './TemplatesPanel';
import OutboxPanel from './OutboxPanel';
import './CommunicationsHub.css';

const TABS = [
  { key: 'inbox', label: 'Inbox', icon: Inbox },
  { key: 'send', label: 'Send', icon: Send },
  { key: 'journey', label: 'Journey', icon: Network },
  { key: 'templates', label: 'Templates', icon: FileText },
  { key: 'outbox', label: 'Outbox', icon: Clock },
];

// Cursor-tracked depth on the cinematic background.
// Single rAF-coalesced listener writes CSS variables --orb-x / --orb-y on the
// Hub root. CSS consumers translate background layers by those variables.
// Respects prefers-reduced-motion (no-op when user opts out).
function useMouseParallax(rootRef, { strength = 8 } = {}) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const reduce = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;

    let frame = 0;
    let pendingX = 0;
    let pendingY = 0;

    const apply = () => {
      frame = 0;
      root.style.setProperty('--orb-x', `${pendingX.toFixed(2)}px`);
      root.style.setProperty('--orb-y', `${pendingY.toFixed(2)}px`);
    };

    const handle = (event) => {
      const rect = root.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (event.clientX - cx) / (rect.width / 2);
      const dy = (event.clientY - cy) / (rect.height / 2);
      pendingX = Math.max(-1, Math.min(1, dx)) * strength;
      pendingY = Math.max(-1, Math.min(1, dy)) * strength;
      if (!frame) frame = window.requestAnimationFrame(apply);
    };

    window.addEventListener('mousemove', handle, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handle);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [rootRef, strength]);
}

// Subtle ambient scroll-parallax on the gold-halo atmospheric overlays.
// Single layer, 0.3x rate. No section-snap, no scroll-jacking. Disabled with
// prefers-reduced-motion.
function useScrollParallax(rootRef, { rate = 0.3 } = {}) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const reduce = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;

    let frame = 0;
    const apply = () => {
      frame = 0;
      const y = (window.scrollY || 0) * rate;
      root.style.setProperty('--ambient-y', `${y.toFixed(2)}px`);
    };

    const handle = () => {
      if (!frame) frame = window.requestAnimationFrame(apply);
    };

    window.addEventListener('scroll', handle, { passive: true });
    apply();
    return () => {
      window.removeEventListener('scroll', handle);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [rootRef, rate]);
}

// FLIP-style sliding tab indicator: measures the active tab's rect, sets
// CSS variables --indicator-x / --indicator-width. CSS animates between values
// over 280ms cubic-bezier(0.22, 1, 0.36, 1).
function useTabIndicator(navRef, activeKey) {
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    let frame = 0;

    const measure = () => {
      frame = 0;
      const active = nav.querySelector(`[data-tab="${activeKey}"]`);
      if (!active) return;
      const navRect = nav.getBoundingClientRect();
      const tabRect = active.getBoundingClientRect();
      nav.style.setProperty('--indicator-x', `${(tabRect.left - navRect.left).toFixed(2)}px`);
      nav.style.setProperty('--indicator-width', `${tabRect.width.toFixed(2)}px`);
    };

    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };

    schedule();

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    if (ro) ro.observe(nav);
    window.addEventListener('resize', schedule);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [navRef, activeKey]);
}

export default function CommunicationsHub({ guests = [], messages = [], onMessagesChanged }) {
  const [activeTab, setActiveTab] = useState('inbox');
  const [sendContext, setSendContext] = useState(null);
  const hubRef = useRef(null);
  const navRef = useRef(null);

  useMouseParallax(hubRef);
  useScrollParallax(hubRef);
  useTabIndicator(navRef, activeTab);

  // Governed hand-off from another module (e.g. Seating "Email roster"): a one-shot prefill stashed
  // in sessionStorage opens the Send composer with the subject/body filled in. The host still picks
  // recipients and confirms send — nothing is sent automatically.
  useEffect(() => {
    let raw = null;
    try { raw = sessionStorage.getItem('communications_seating_prefill'); } catch (e) { raw = null; }
    if (!raw) return;
    try {
      const p = JSON.parse(raw);
      setSendContext({
        type: SEND_ENTRY_CONTEXT_TYPES.GUEST_LIST_SELECTION,
        purpose: p.purpose || 'custom',
        channel: p.channel || 'email',
        customSubject: p.subject || '',
        customBody: p.body || '',
      });
      setActiveTab('send');
    } catch (e) { /* ignore malformed prefill */ }
    try { sessionStorage.removeItem('communications_seating_prefill'); } catch (e) { /* */ }
  }, []);

  const unreadCount = useMemo(
    () => messages.filter(message => message.status === 'unread').length,
    [messages]
  );

  const openReplyComposer = useCallback((message) => {
    const email = String(message?.email || '').trim().toLowerCase();
    const guest = guests.find(item => String(item.email || '').trim().toLowerCase() === email);
    setSendContext({
      type: SEND_ENTRY_CONTEXT_TYPES.GUEST_DETAIL_FOLLOW_UP,
      purpose: 'follow_up',
      channel: 'email',
      guestIds: guest ? [String(guest.id || guest._id || '')].filter(Boolean) : [],
      sourceMessageId: message?.id || message?._id || '',
    });
    setActiveTab('send');
  }, [guests]);

  const renderPanel = () => {
    switch (activeTab) {
      case 'send':
        return <SendPanel key={sendContext ? JSON.stringify(sendContext) : 'send'} guests={guests} initialContext={sendContext} onOpenOutbox={() => setActiveTab('outbox')} />;
      case 'journey':
        return <JourneyPanel onPrepareSend={(context) => {
          setSendContext({ type: SEND_ENTRY_CONTEXT_TYPES.JOURNEY_MILESTONE, ...context });
          setActiveTab('send');
        }} onOpenOutbox={() => setActiveTab('outbox')} />;
      case 'templates':
        return <TemplatesPanel />;
      case 'outbox':
        return <OutboxPanel onUseAgain={(context) => {
          setSendContext(context);
          setActiveTab('send');
        }} />;
      case 'inbox':
      default:
        return <InboxPanel messages={messages} unreadCount={unreadCount} guests={guests} onMessagesChanged={onMessagesChanged} onPrepareReply={openReplyComposer} />;
    }
  };

  return (
    <section className="communications-hub" data-testid="communications-hub" ref={hubRef}>
      <header className="communications-hub__header">
        <div>
          <p className="communications-hub__eyebrow">M-Vitations</p>
          <h1>Communications Hub</h1>
          <p>Central workspace for guest messages, sending, journeys, templates, and outbound history.</p>
        </div>
      </header>

      <nav className="communications-hub__tabs" aria-label="Communications sections" ref={navRef}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              data-tab={tab.key}
              className={`communications-hub__tab ${active ? 'communications-hub__tab--active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
              aria-pressed={active}
              data-testid={`communications-tab-${tab.key}`}
            >
              <Icon size={18} aria-hidden="true" />
              <span>{tab.label}</span>
              {tab.key === 'inbox' && unreadCount > 0 && (
                <strong className="communications-hub__badge">{unreadCount}</strong>
              )}
            </button>
          );
        })}
        <span className="communications-hub__tab-indicator" aria-hidden="true" />
      </nav>

      <div className="communications-hub__panel" key={activeTab}>
        {renderPanel()}
      </div>
    </section>
  );
}
